import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import * as authService from './services/authService';
import * as profileService from './services/profileService';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SignupPaymentGuard from './components/SignupPaymentGuard';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AdminUserLogin from './pages/AdminUserLogin';
import AdminUserSignup from './pages/AdminUserSignup';
import Profile from './pages/Profile';
// import SubmitArticles from './pages/SubmitArticles';
// import MyArticles from './pages/MyArticles';  
import SubmitAbstract from './pages/SubmitAbstract';
import MyAbstract from './pages/MyAbstract';
import MyPayment from './pages/MyPayment';
// import AssignArticle from './pages/AssignArticle';
import AssignAbstract from './pages/AssignAbstract';
import SubmitPaper from './pages/SubmitPaper';
import MyPaper from './pages/MyPaper';
import AssignPaper from './pages/AssignPaper';
// import AssignedArticle from './pages/AssignedArticle';
import AssignedAbstract from './pages/AssignedAbstract';
import AssignedPaper from './pages/AssignedPaper';

// Professor Management
import ProfessorManagement from './pages/ProfessorManagement';
import ReviewerManagement from './pages/ReviewerManagement';
import EditorialManagement from './pages/EditorialManagement';

// Master Management
import CreateSubject from './pages/CreateSubject';
import CreateStream from './pages/CreateStream';
import CreateSpecialization from './pages/CreateSpecialization';
import CreateLanguage from './pages/CreateLanguage';

// Young India components
import CreateArticleTitle from './pages/CreateArticleTitle';
import SubmitArticleYoungIndia from './pages/SubmitArticleYoungIndia';
import MySubmittedArticlesYoungIndia from './pages/MySubmittedArticlesYoungIndia';
// import AssignedTitleForReviewer from './pages/AssignedTitleForReviewer';
// import AssignedArticlesYoungIndia from './pages/AssignedArticlesYoungIndia';
// import CustomSubmission from './pages/CustomSubmission';
import AllArticlesYoungIndia from './pages/AllArticlesYoungIndia';
import SelectedStudents from './pages/SelectedStudents';
import YoungIndiaStudentManagement from './pages/YoungIndiaStudentManagement';
import AboutPaperPortal from './pages/AboutPaperPortal';
import EditorialBoard from './pages/EditorialBoard';
import AuthorizedNumbers from './pages/AuthorizedNumbers';
import AvailableSubEBUsers from './pages/AvailableSubEBUsers';
import AdminSubEBManagement from './pages/AdminSubEBManagement';
import AdminPaymentManagement from './pages/AdminPaymentManagement';
import AdminSettings from './pages/AdminSettings';
import AdminYIPaymentSettings from './pages/AdminYIPaymentSettings';
import EBManageTitles from './pages/EBManageTitles';
import AdminPendingTitles from './pages/AdminPendingTitles';

// Legal Pages
import TermsAndConditions from './pages/TermsAndConditions';
import RefundPolicy from './pages/RefundPolicy';
import ShippingPolicy from './pages/ShippingPolicy';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Component to scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Standard window scroll
    window.scrollTo(0, 0);

    // Explicitly scroll body and documentElement if they are the containers
    // This is important because index.css sets overflow: hidden on html and overflow-y: auto on body
    if (document.body) {
      document.body.scrollTo(0, 0);
      document.body.scrollTop = 0;
    }
    if (document.documentElement) {
      document.documentElement.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
    }
  }, [pathname]);

  return null;
}

// Component to conditionally render Navbar
function ConditionalNavbar() {
  const location = useLocation();
  const hideNavbarPaths = ['/login', '/signup', '/employeelogin', '/employeesignup'];

  if (hideNavbarPaths.includes(location.pathname)) {
    return null;
  }

  return <Navbar />;
}

// Component to conditionally render Footer
function ConditionalFooter() {
  const location = useLocation();
  const hideFooterPaths = ['/login', '/signup', '/employeelogin', '/employeesignup'];

  if (hideFooterPaths.includes(location.pathname)) {
    return null;
  }

  return <Footer />;
}

function App() {
  const [isInitializing, setIsInitializing] = React.useState(true);

  // Persistent Login: Try to refresh token and fetch profile on app load
  useEffect(() => {
    const tryAutoLogin = async () => {
      try {
        const refreshToken = authService.getRefreshToken();
        let user = authService.getCurrentUser();

        // Preemptive refresh only if we HAVE a refresh token but NO access token
        if (refreshToken && !authService.getAccessToken()) {
          try {
            await authService.refreshAccessToken();
            user = authService.getCurrentUser();
          } catch (error) {
            console.error('Auto-login failed:', error);
          }
        }

        // Build user profile if logged in to ensure permissions are up to date
        if (authService.isLoggedIn()) {
          try {
            const profileData = await profileService.getProfile();
            if (profileData && profileData.success && profileData.data) {
              const pData = profileData.data;
              const personal = pData.personal || {};

              const fullName = pData.full_name ||
                personal.full_name ||
                pData.name ||
                personal.name ||
                `${personal.first_name || ''} ${personal.last_name || ''}`.trim();

              if (fullName) {
                const permissions = pData.permissions || {};
                const updatedUser = {
                  ...user,
                  ...pData,
                  is_reviewer: pData.is_reviewer ?? permissions.is_reviewer ?? user.is_reviewer,
                  is_editorial_board: pData.is_editorial_board ?? permissions.is_editorial_board ?? user.is_editorial_board,
                  is_verified_sub_eb: pData.is_verified_sub_eb ?? permissions.is_verified_sub_eb ?? user.is_verified_sub_eb,
                  full_name: fullName,
                  name: fullName,
                  // Always sync payment_status from DB so PENDING users remain restricted
                  // even if admin later disables global pay setting
                  payment_status: pData.payment_status ?? user.payment_status
                };

                const wasInLocalStorage = !!localStorage.getItem('paperPortalUser');
                authService.storeSession(updatedUser, null, null, wasInLocalStorage);
              }
            }
          } catch (error) {
            console.error('Failed to fetch profile during auto-login:', error);
          }
          window.dispatchEvent(new Event('userLogin'));
        }
      } finally {
        setIsInitializing(false);
      }
    };

    tryAutoLogin();
  }, []);

  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-primary-blue border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-500 font-medium">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App flex flex-col min-h-screen">
        <ScrollToTop />
        <ConditionalNavbar />
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<SignupPaymentGuard><Home /></SignupPaymentGuard>} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/employeelogin" element={<AdminUserLogin />} />
            <Route path="/about-paper-portal" element={<AboutPaperPortal />} />
            <Route path="/editorial-board" element={<EditorialBoard />} />
            <Route path="/employeesignup" element={<AdminUserSignup />} />
            <Route path="/profile" element={<Profile />} />
            {/* <Route path="/submit-articles" element={<SubmitArticles />} />
            <Route path="/my-articles" element={<MyArticles />} /> */}
            <Route path="/submit-abstract" element={<SignupPaymentGuard><SubmitAbstract /></SignupPaymentGuard>} />
            <Route path="/my-abstract" element={<SignupPaymentGuard><MyAbstract /></SignupPaymentGuard>} />
            <Route path="/my-payment" element={<SignupPaymentGuard><MyPayment /></SignupPaymentGuard>} />
            {/* <Route path="/assign-article" element={<AssignArticle />} /> */}
            <Route path="/assign-abstract" element={<SignupPaymentGuard><AssignAbstract /></SignupPaymentGuard>} />
            <Route path="/submit-paper" element={<SignupPaymentGuard><SubmitPaper /></SignupPaymentGuard>} />
            <Route path="/my-paper" element={<SignupPaymentGuard><MyPaper /></SignupPaymentGuard>} />
            <Route path="/assign-paper" element={<SignupPaymentGuard><AssignPaper /></SignupPaymentGuard>} />
            {/* <Route path="/assigned-article" element={<AssignedArticle />} /> */}
            <Route path="/assigned-abstract" element={<SignupPaymentGuard><AssignedAbstract /></SignupPaymentGuard>} />
            <Route path="/assigned-paper" element={<SignupPaymentGuard><AssignedPaper /></SignupPaymentGuard>} />

            {/* Professor Management Route */}
            <Route path="/professor-management" element={<SignupPaymentGuard><ProfessorManagement /></SignupPaymentGuard>} />
            <Route path="/reviewer-management" element={<SignupPaymentGuard><ReviewerManagement /></SignupPaymentGuard>} />
            <Route path="/editorial-management" element={<SignupPaymentGuard><EditorialManagement /></SignupPaymentGuard>} />

            {/* Master Management Routes */}
            <Route path="/create-subject" element={<SignupPaymentGuard><CreateSubject /></SignupPaymentGuard>} />
            <Route path="/create-stream" element={<SignupPaymentGuard><CreateStream /></SignupPaymentGuard>} />
            <Route path="/create-specialization" element={<SignupPaymentGuard><CreateSpecialization /></SignupPaymentGuard>} />
            <Route path="/create-language" element={<SignupPaymentGuard><CreateLanguage /></SignupPaymentGuard>} />
            <Route path="/authorized-numbers" element={<SignupPaymentGuard><AuthorizedNumbers /></SignupPaymentGuard>} />

            {/* Young India Routes */}
            <Route path="/create-title" element={<SignupPaymentGuard><CreateArticleTitle /></SignupPaymentGuard>} />
            <Route path="/submit-article-young-india" element={<SignupPaymentGuard><SubmitArticleYoungIndia /></SignupPaymentGuard>} />
            {/* <Route path="/custom-submission" element={<CustomSubmission />} /> */}
            <Route path="/my-submitted-articles-young-india" element={<SignupPaymentGuard><MySubmittedArticlesYoungIndia /></SignupPaymentGuard>} />
            {/* <Route path="/assigned-reviewer" element={<AssignedTitleForReviewer />} /> */}
            {/* <Route path="/assigned-articles" element={<AssignedArticlesYoungIndia />} /> */}
            <Route path="/all-articles-young-india" element={<SignupPaymentGuard><AllArticlesYoungIndia /></SignupPaymentGuard>} />
            <Route path="/selected-students" element={<SignupPaymentGuard><SelectedStudents /></SignupPaymentGuard>} />
            <Route path="/yi-student-management" element={<YoungIndiaStudentManagement />} />
            <Route path="/eb/manage-titles" element={<SignupPaymentGuard><EBManageTitles /></SignupPaymentGuard>} />
            <Route path="/admin/pending-titles" element={<SignupPaymentGuard><AdminPendingTitles /></SignupPaymentGuard>} />

            {/* Sub-EB Management Routes */}
            <Route path="/sub-eb-selection" element={<SignupPaymentGuard><AvailableSubEBUsers /></SignupPaymentGuard>} />
            <Route path="/admin-sub-eb-requests" element={<SignupPaymentGuard><AdminSubEBManagement /></SignupPaymentGuard>} />
            <Route path="/admin-payment-management" element={<SignupPaymentGuard><AdminPaymentManagement /></SignupPaymentGuard>} />
            <Route path="/admin-settings" element={<SignupPaymentGuard><AdminSettings /></SignupPaymentGuard>} />
            <Route path="/admin-yi-payment-settings" element={<SignupPaymentGuard><AdminYIPaymentSettings /></SignupPaymentGuard>} />

            {/* Legal Routes */}
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          </Routes>
        </main>
        <ConditionalFooter />
      </div>
    </Router>
  );
}

export default App;